/**
 * Fond SKILLCARD d'un défi (migration 0699). Sans photo ni vidéo, la carte du
 * défi prend le fond d'une SKILLCARD : celle choisie ici (à la création par
 * l'auteur, ou après par l'admin), sinon celle reliée à la question partagée
 * (calculée par le site).
 */
import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { CardColorTheme, CardRarity } from '@jeraisonne/db/cards';
import { supabase } from '@/lib/supabase';
import type { SkillcardPickerItem } from '@/features/skillcards/skillcard-picker';

export interface ChallengeBackgroundOptions {
  items: SkillcardPickerItem[];
  /** Cartes reliées à l'exercice (proposées en tête). */
  linkedIds: string[];
}

interface Row {
  id: string;
  title: string;
  levels: unknown;
  rarity: CardRarity;
  color_theme: CardColorTheme;
  creature: unknown;
  domain_label: string | null;
  linked: boolean;
}

/** Réponse de `get_challenge_background_cards` → éléments du sélecteur. */
export function toBackgroundOptions(rows: unknown): ChallengeBackgroundOptions {
  const list = Array.isArray(rows) ? (rows as Row[]) : [];
  const items = list.map((r) => ({
    id: r.id,
    title: r.title,
    levels: Array.isArray(r.levels)
      ? r.levels.filter((l): l is string => typeof l === 'string')
      : [],
    rarity: r.rarity,
    colorTheme: r.color_theme,
    creature: r.creature ?? null,
    domainLabel: r.domain_label ?? null,
  }));
  return { items, linkedIds: list.filter((r) => r.linked).map((r) => r.id) };
}

/** Sections du sélecteur : cartes de l'exercice d'abord, puis toutes les autres. */
export function backgroundSections(options: ChallengeBackgroundOptions) {
  const linked = new Set(options.linkedIds);
  const mine = options.items.filter((c) => linked.has(c.id));
  const others = options.items.filter((c) => !linked.has(c.id));
  return [
    ...(mine.length ? [{ key: 'linked', label: 'SKILLCARDS de cet exercice', items: mine }] : []),
    { key: 'all', label: 'Toutes les SKILLCARDS', items: others },
  ];
}

export function useChallengeBackgroundOptions(exerciseId: string | null | undefined) {
  return useQuery({
    queryKey: ['challenge-background-cards', exerciseId],
    enabled: !!exerciseId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const db = supabase as unknown as SupabaseClient;
      const { data, error } = await db.rpc('get_challenge_background_cards', {
        p_exercise_id: exerciseId,
      });
      if (error) throw new Error(error.message);
      return toBackgroundOptions(data);
    },
  });
}

/** Pose (ou retire, `null`) la SKILLCARD de fond d'un défi. */
export async function setChallengeBackground(
  match: { id: string } | { slug: string },
  cardId: string | null,
): Promise<void> {
  const db = supabase as unknown as SupabaseClient;
  const q = db.from('challenge_posts').update({ background_card_id: cardId });
  const { error } = await ('id' in match
    ? q.eq('id', match.id)
    : q.eq('challenge_slug', match.slug));
  if (error) {
    throw new Error(
      error.message.includes('challenge_background_not_published')
        ? 'Cette SKILLCARD n’est pas publiée : choisis-en une autre.'
        : error.message,
    );
  }
}
